import React from "react";
import {
  Box,
  Chip,
  IconButton,
  Link,
  Paper,
  Table,
  TableBody,
  TableContainer,
  TableHead,
  TableRow,
  TableSortLabel,
  Theme,
  Tooltip,
  Typography,
  useTheme,
} from "@mui/material";
import InfoOutlinedIcon from "@mui/icons-material/InfoOutlined";
import AppTableHeaderCell from "../../../../../views/components/common/pages/AppTableHeaderCell";
import AppTablePagenation from "../../../../../views/components/common/pages/AppTablePagenation";
import { SortType } from "../../../../../models/SortType";
import { formatDate } from "../../../../../views/components/common/functions/formatDate";
import AppTableRowCell from "../../../../../views/components/common/pages/AppTableRowCell";
import { Leads } from "../../../../../models/Leads/leads";

interface LeadTableProp {
  maxHeight: number;
  leads: Leads[];
  limit: number;
  page_no: number;
  total_pages: number;
  total_count: number;
  sortBy: string;
  sortType: SortType;
  onPageChange: (
    event: React.MouseEvent<HTMLButtonElement> | null,
    page: number
  ) => void;
  onRowsPerPageChange: (limit: number) => void;
  onSortChange: (column: string, sortType: SortType) => void;
  onViewDetails: (lead: Leads) => void;
}

const LeadTable = ({
  maxHeight,
  leads,
  page_no,
  limit,
  total_count,
  sortBy,
  sortType,
  onPageChange,
  onRowsPerPageChange,
  onSortChange,
  onViewDetails,
}: LeadTableProp) => {
  const theme = useTheme<Theme>();

  const handleRowsChange = (value: number) => {
    onPageChange(null, 1);
    onRowsPerPageChange(value);
  };

  const renderSortIcon = (property: string) => {
    if (sortBy === property) {
      return sortType === SortType.ASC ? "↑" : "↓";
    }
    return "↑↓";
  };

  return (
    <Paper
      sx={{
        borderRadius: "8px",
        boxShadow: "0 2px 8px rgba(0, 0, 0, 0.1)",
        overflow: "hidden",
      }}
    >
      {leads.length > 0 ? (
        <Box>
          <TableContainer sx={{ maxHeight: maxHeight }}>
            <Table stickyHeader>
              <TableHead sx={{ position: "relative", zIndex: 30 }}>
                <TableRow>
                  <AppTableHeaderCell
                    sx={{
                      textAlign: "center",
                      fontSize: 14,
                      position: "sticky",
                      left: 0,
                      top: 0,
                      zIndex: 30,
                      minWidth: "60px",
                      backgroundColor: theme.palette.background.paper,
                      isolation: "isolate",
                    }}
                  >
                    <TableSortLabel
                      active={sortBy === "id"}
                      direction={sortType === SortType.ASC ? "asc" : "desc"}
                      onClick={() =>
                        onSortChange(
                          "id",
                          sortBy === "id" && sortType === SortType.DESC
                            ? SortType.ASC
                            : SortType.DESC
                        )
                      }
                      IconComponent={() => <span>{renderSortIcon("id")}</span>}
                      sx={{ fontSize: "12px" }}
                    >
                      ID
                    </TableSortLabel>
                  </AppTableHeaderCell>
                  <AppTableHeaderCell
                    sx={{
                      textAlign: "center",
                      fontSize: 14,
                      minWidth: "60px",
                    }}
                  >
                    <TableSortLabel
                      active={sortBy === "kylas_lead_id"}
                      direction={sortType === SortType.ASC ? "asc" : "desc"}
                      onClick={() =>
                        onSortChange(
                          "kylas_lead_id",
                          sortBy === "kylas_lead_id" &&
                            sortType === SortType.DESC
                            ? SortType.ASC
                            : SortType.DESC
                        )
                      }
                      IconComponent={() => (
                        <span>{renderSortIcon("kylas_lead_id")}</span>
                      )}
                      sx={{ fontSize: "12px" }}
                    >
                      Kylas ID
                    </TableSortLabel>
                  </AppTableHeaderCell>

                  <AppTableHeaderCell
                    sx={{
                      textAlign: "center",
                      fontSize: 14,
                      minWidth: "60px",
                    }}
                  >
                    <TableSortLabel
                      active={sortBy === "name"}
                      direction={sortType === SortType.ASC ? "asc" : "desc"}
                      onClick={() =>
                        onSortChange(
                          "name",
                          sortBy === "name" && sortType === SortType.DESC
                            ? SortType.ASC
                            : SortType.DESC
                        )
                      }
                      IconComponent={() => (
                        <span>{renderSortIcon("name")}</span>
                      )}
                      sx={{ fontSize: "12px" }}
                    >
                      Name
                    </TableSortLabel>
                  </AppTableHeaderCell>
                  <AppTableHeaderCell title="Source" fontSize="0.8rem" />

                  <AppTableHeaderCell title="Email" fontSize="0.8rem" />

                  <AppTableHeaderCell title="Mobile" fontSize="0.8rem" />

                  <AppTableHeaderCell title="Company" fontSize="0.8rem" />

                  <AppTableHeaderCell title="Company Size" fontSize="0.8rem" />

                  <AppTableHeaderCell title="Department" fontSize="0.8rem" />

                  <AppTableHeaderCell title="Form Page" fontSize="0.8rem" />

                  <AppTableHeaderCell fontSize="0.8rem">
                    <TableSortLabel
                      active={sortBy === "created_date"}
                      direction={sortType === SortType.ASC ? "asc" : "desc"}
                      onClick={() =>
                        onSortChange(
                          "created_date",
                          sortBy === "created_date" &&
                            sortType === SortType.DESC
                            ? SortType.ASC
                            : SortType.DESC
                        )
                      }
                      IconComponent={() => (
                        <span>{renderSortIcon("created_date")}</span>
                      )}
                      sx={{ fontSize: "0.8rem" }}
                    >
                      Created Date
                    </TableSortLabel>
                  </AppTableHeaderCell>
                  <AppTableHeaderCell
                    title="Actions"
                    fontSize="0.8rem"
                    minWidth="90px"
                  />
                </TableRow>
              </TableHead>
              <TableBody>
                {leads.map((lead) => {
                  return (
                    <TableRow
                      key={lead.id}
                      hover
                      sx={{
                        "&:nth-of-type(odd)": {
                          backgroundColor: theme.palette.action.hover,
                        },
                      }}
                    >
                      <AppTableRowCell
                        sx={{
                          fontSize: "12px",
                          textAlign: "center",
                          left: 0,
                          position: "sticky",
                          zIndex: 10,
                          boxShadow: "10px 0 20px -12px rgba(115,115,115,0.3)",
                          backgroundColor: theme.palette.background.paper,
                          fontWeight: 600,
                          minWidth: "60px",
                        }}
                      >
                        {/* <Link
                          href={`/leads`}
                          target="_blank"
                          sx={{
                            textDecoration: "underline",
                            color:
                              theme.palette.customColors?.text ||
                              theme.palette.primary.main,
                            "&:hover": { textDecoration: "underline" },
                          }}
                        > */}
                          {lead.id}
                        {/* </Link> */}
                      </AppTableRowCell>

                      <AppTableRowCell
                        sx={{ fontSize: "12px", textAlign: "center" }}
                      >
                        {lead.kylas_lead_id || "-"}
                      </AppTableRowCell>

                      <AppTableRowCell
                        sx={{ fontSize: "12px", textAlign: "center" }}
                      >
                        {lead.name || "-"}
                      </AppTableRowCell>
                      <AppTableRowCell
                        sx={{ fontSize: "12px", textAlign: "center" }}
                      >
                        {lead.source || "-"}
                      </AppTableRowCell>

                      <AppTableRowCell
                        sx={{ fontSize: "12px", textAlign: "center" }}
                      >
                        {lead.email_id || "-"}
                      </AppTableRowCell>

                      <AppTableRowCell
                        sx={{ fontSize: "12px", textAlign: "center" }}
                      >
                        {lead.mobile || "-"}
                      </AppTableRowCell>

                      <AppTableRowCell
                        sx={{ fontSize: "12px", textAlign: "center" }}
                      >
                        {lead.company_name || "-"}
                      </AppTableRowCell>

                      <AppTableRowCell
                        sx={{ fontSize: "12px", textAlign: "center" }}
                      >
                        {lead.company_size || "-"}
                      </AppTableRowCell>

                      <AppTableRowCell
                        sx={{ fontSize: "12px", textAlign: "center" }}
                      >
                        {lead.department_name || "-"}
                      </AppTableRowCell>

                      <AppTableRowCell
                        sx={{ fontSize: "12px", textAlign: "center" }}
                      >
                        {lead.form_submission_page_url ? (
                          <Link
                            href={lead.form_submission_page_url}
                            target="_blank"
                            sx={{
                              textDecoration: "underline",
                              color:
                                theme.palette.customColors?.text ||
                                theme.palette.primary.main,
                              "&:hover": { textDecoration: "underline" },
                            }}
                          >
                            View Page
                          </Link>
                        ) : (
                          "-"
                        )}
                      </AppTableRowCell>

                      <AppTableRowCell
                        sx={{ fontSize: "12px", textAlign: "center" }}
                      >
                        {formatDate(lead.created_date)}
                      </AppTableRowCell>
                      <AppTableRowCell sx={{ textAlign: "center" }}>
                        <Tooltip title="View Details">
                          <IconButton
                            size="small"
                            onClick={() => onViewDetails(lead)}
                          >
                            <InfoOutlinedIcon fontSize="small" />
                          </IconButton>
                        </Tooltip>
                      </AppTableRowCell>
                    </TableRow>
                  );
                })}
              </TableBody>
            </Table>
          </TableContainer>
          <AppTablePagenation
            fontSize="12px"
            pageNo={page_no}
            limit={limit}
            totalCount={total_count}
            onPageChange={onPageChange}
            handleRowsChange={handleRowsChange}
          />
        </Box>
      ) : (
        <Box
          sx={{
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            height: "200px",
            color: theme.palette.text.secondary,
          }}
        >
          <Typography variant="body1">No data available</Typography>
        </Box>
      )}
    </Paper>
  );
};

export default LeadTable;
